import React, { ChangeEvent, ReactElement, useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../hooks/hooks';
import { RootState } from '../../../redux/reducers';
import { addOrUpdateMultipleReduxGoods } from '../../../redux/actions/goodsActions';
import { updateReduxSite } from '../../../redux/actions/siteActions';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { renderTimeViewClock } from '@mui/x-date-pickers/timeViewRenderers';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/sr';
import {
    Box,
    Typography,
    DialogContent,
    Grid2,
    TextField,
    Button,
    InputAdornment,
    SelectChangeEvent,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    CircularProgress,
    Table,
    TableHead,
    TableRow,
    TableBody,
    TableCell,
    TableContainer,
    Paper,
    IconButton,
    Tooltip,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { useParams } from 'react-router-dom';
import { BulkInstallGoodsItem, bulkInstallGoodsOnSite, WarehouseClient } from '../../../api/Extensions';
import { ArticleDto, GoodsStatus, SituationDto } from '../../../api/ClientApi';
import { DimensionTypeDimensionSR } from '../../../common/Types';
import { StyledTableCell } from '../../../common/components/styled/StyledTable';
import { formatNumber } from '../../../common/Extensions';

import { FormDialogTitle } from '../../../common/components/form/FormPrimitives';

import { ResponsiveDialog } from '../../../common/components/mobile/MobilePrimitives';

type BulkInstallGoodsFormProps = {
    open: boolean;
    closeForm(): void;
};

type Row = {
    articleId: number;
    article: ArticleDto;
    available: number;
};

export default function BulkInstallGoodsForm(props: BulkInstallGoodsFormProps): ReactElement {
    const { open, closeForm } = props;

    const params = useParams();
    const siteId = params.siteId && !isNaN(+params.siteId) && +params.siteId > 0 ? +params.siteId : undefined;

    const dispatch = useAppDispatch();

    const articles = useAppSelector((state: RootState) => state.articles.articles);
    const goods = useAppSelector((state: RootState) => state.goods.goods);

    const [situations, setSituations] = useState<SituationDto[]>([]);
    const [executionTime, setExecutionTime] = useState<Date | undefined>(undefined);
    const [situationId, setSituationId] = useState<number | undefined>(undefined);
    const [reason, setReason] = useState<string>('');
    const [articleFilter, setArticleFilter] = useState<string>('');
    const [quantities, setQuantities] = useState<Record<number, number>>({});
    const [saving, setSaving] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

    useEffect(() => {
        if (open && siteId) {
            setExecutionTime(new Date());
            WarehouseClient.getAllSituationsForSite(siteId).then((res) => {
                setSituations(res);
            });
        }
    }, [open, siteId]);

    const rows = useMemo<Row[]>(() => {
        if (!goods || !articles || !siteId) {
            return [];
        }

        const aggregate = new Map<number, number>();

        for (const g of goods) {
            if (g.siteId !== siteId || g.goodsStatus !== GoodsStatus.InStock) {
                continue;
            }
            const aid = g.article?.id;
            if (!aid) {
                continue;
            }
            aggregate.set(aid, (aggregate.get(aid) ?? 0) + (g.quantity ?? 0));
        }

        const result: Row[] = [];
        aggregate.forEach((available, articleId) => {
            if (available <= 0) {
                return;
            }
            const article = articles.find((a) => a.id === articleId);
            if (!article) {
                return;
            }
            result.push({ articleId, article, available });
        });

        result.sort((a, b) => {
            const ta = (a.article.title ?? a.article.manufacturer ?? '').toLowerCase();
            const tb = (b.article.title ?? b.article.manufacturer ?? '').toLowerCase();
            return ta.localeCompare(tb);
        });

        return result;
    }, [goods, articles, siteId]);

    const filteredRows = useMemo(() => {
        const f = articleFilter.trim().toLowerCase();
        if (!f) {
            return rows;
        }
        return rows.filter((r) => {
            return (
                (r.article.title ?? '').toLowerCase().includes(f) ||
                (r.article.manufacturer ?? '').toLowerCase().includes(f) ||
                (r.article.innerCode ?? '').toString().toLowerCase().includes(f) ||
                (r.article.outterCode ?? '').toString().toLowerCase().includes(f)
            );
        });
    }, [rows, articleFilter]);

    const itemsToSubmit: BulkInstallGoodsItem[] = useMemo(() => {
        return Object.entries(quantities)
            .map(([k, v]) => ({ articleId: +k, quantity: v }))
            .filter((i) => i.quantity > 0);
    }, [quantities]);

    const totalLines = itemsToSubmit.length;

    const overstockedArticleId = useMemo(() => {
        for (const item of itemsToSubmit) {
            const row = rows.find((r) => r.articleId === item.articleId);
            if (!row || item.quantity > row.available) {
                return item.articleId;
            }
        }
        return undefined;
    }, [itemsToSubmit, rows]);

    const canSave = !!siteId && !!executionTime && totalLines > 0 && overstockedArticleId === undefined && !saving;

    const handleClose = () => {
        setExecutionTime(undefined);
        setSituationId(undefined);
        setReason('');
        setArticleFilter('');
        setQuantities({});
        setErrorMessage(undefined);
        setSaving(false);
        closeForm();
    };

    const handleQuantityChange = (articleId: number, value: string) => {
        if (value === '') {
            setQuantities((prev) => {
                const next = { ...prev };
                delete next[articleId];
                return next;
            });
            return;
        }
        const n = +value;
        if (isNaN(n) || n < 0) {
            return;
        }
        setQuantities((prev) => ({ ...prev, [articleId]: n }));
    };

    const handleClearQuantity = (articleId: number) => {
        setQuantities((prev) => {
            const next = { ...prev };
            delete next[articleId];
            return next;
        });
    };

    const handleSelectSituation = (event: SelectChangeEvent<number | ''>) => {
        const value = event.target.value;
        if (value === '' || value === undefined) {
            setSituationId(undefined);
            return;
        }
        const n = +value;
        if (!isNaN(n) && n > 0) {
            setSituationId(n);
        }
    };

    const handleExecutionTimeChange = (value: Dayjs | null) => {
        setExecutionTime(value ? value.toDate() : undefined);
    };

    const handleSave = async () => {
        if (!canSave || !siteId || !executionTime) {
            return;
        }

        setSaving(true);
        setErrorMessage(undefined);

        try {
            const updated = await bulkInstallGoodsOnSite({
                goodsOriginSiteId: siteId,
                usedOnSituationId: situationId,
                executionTime,
                reason: reason || undefined,
                items: itemsToSubmit,
            });

            dispatch(addOrUpdateMultipleReduxGoods(updated));
            dispatch(updateReduxSite(siteId));

            handleClose();
        } catch (err: any) {
            const msg =
                err?.messageValue || err?.MessageValue || err?.message || 'Greška prilikom čuvanja. Pokušajte ponovo.';
            setErrorMessage(msg);
            setSaving(false);
        }
    };

    return (
        <ResponsiveDialog maxWidth="lg" fullWidth open={open} onClose={handleClose}>
            <FormDialogTitle>Ugradi više artikala</FormDialogTitle>
            <DialogContent dividers sx={{ px: 3, pt: 3 }}>
                <Grid2 container spacing={2}>
                    <Grid2 size={{ xs: 12, sm: 4 }}>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="sr">
                            <DateTimePicker
                                label="Vreme izvršenja"
                                viewRenderers={{
                                    hours: renderTimeViewClock,
                                    minutes: renderTimeViewClock,
                                    seconds: renderTimeViewClock,
                                }}
                                value={executionTime ? dayjs(executionTime) : null}
                                onChange={handleExecutionTimeChange}
                                slotProps={{ textField: { fullWidth: true, required: true } }}
                            />
                        </LocalizationProvider>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 4 }}>
                        <FormControl fullWidth>
                            <InputLabel id="bulk-situation-label">Situacija</InputLabel>
                            <Select
                                labelId="bulk-situation-label"
                                value={situationId ?? ''}
                                label="Situacija"
                                onChange={handleSelectSituation}
                            >
                                <MenuItem value="">
                                    <em>—</em>
                                </MenuItem>
                                {situations?.map((situation) => (
                                    <MenuItem
                                        key={(situation?.id ?? Math.random()) + (situation?.title ?? '')}
                                        value={situation.id}
                                    >
                                        {situation.title}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 4 }}>
                        <TextField
                            fullWidth
                            label="Razlog (opciono)"
                            value={reason}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => setReason(e.target.value)}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12 }}>
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="Pretraga artikla (naziv, proizvođač, šifra)"
                            value={articleFilter}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => setArticleFilter(e.target.value)}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12 }}>
                        <TableContainer component={Paper} sx={{ maxHeight: 450 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <StyledTableCell>Interni kod</StyledTableCell>
                                        <StyledTableCell>Naziv</StyledTableCell>
                                        <StyledTableCell>Proizvođač</StyledTableCell>
                                        <StyledTableCell align="right">Na stanju</StyledTableCell>
                                        <StyledTableCell align="right" sx={{ width: 200 }}>
                                            Količina za ugradnju
                                        </StyledTableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredRows.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={5} align="center">
                                                <Typography variant="body2" color="text.secondary">
                                                    Nema artikala na stanju.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {filteredRows.map((row) => {
                                        const value = quantities[row.articleId];
                                        const isOver = value !== undefined && value > row.available;
                                        const unit = row.article.dimensionType
                                            ? DimensionTypeDimensionSR[row.article.dimensionType]
                                            : 'kom.';
                                        return (
                                            <TableRow key={row.articleId} hover>
                                                <TableCell>{row.article.innerCode ?? '/'}</TableCell>
                                                <TableCell>{row.article.title ?? '/'}</TableCell>
                                                <TableCell>{row.article.manufacturer ?? '/'}</TableCell>
                                                <TableCell align="right">
                                                    {formatNumber(row.available)} {unit}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <TextField
                                                        size="small"
                                                        type="number"
                                                        error={isOver}
                                                        value={value ?? ''}
                                                        onChange={(e) =>
                                                            handleQuantityChange(row.articleId, e.target.value)
                                                        }
                                                        slotProps={{
                                                            input: {
                                                                endAdornment: (
                                                                    <InputAdornment position="end">
                                                                        <Box
                                                                            sx={{
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                gap: 0.5,
                                                                            }}
                                                                        >
                                                                            <Typography
                                                                                variant="caption"
                                                                                color="text.secondary"
                                                                            >
                                                                                {unit}
                                                                            </Typography>
                                                                            {value !== undefined && (
                                                                                <Tooltip title="Obriši">
                                                                                    <IconButton
                                                                                        size="small"
                                                                                        onClick={() =>
                                                                                            handleClearQuantity(
                                                                                                row.articleId,
                                                                                            )
                                                                                        }
                                                                                    >
                                                                                        <ClearIcon fontSize="inherit" />
                                                                                    </IconButton>
                                                                                </Tooltip>
                                                                            )}
                                                                        </Box>
                                                                    </InputAdornment>
                                                                ),
                                                            },
                                                            htmlInput: {
                                                                inputMode: 'decimal',
                                                                min: 0,
                                                                max: row.available,
                                                            },
                                                        }}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Grid2>
                    <Grid2 size={{ xs: 12 }} sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <Typography variant="body2" color="text.secondary">
                            Označeno: <strong>{totalLines}</strong> artikala
                        </Typography>
                        {overstockedArticleId !== undefined && (
                            <Typography color="error" variant="body2">
                                *količina prelazi raspoloživo stanje za neki artikal
                            </Typography>
                        )}
                        {!executionTime && (
                            <Typography color="error" variant="body2">
                                *vreme izvršenja je obavezno
                            </Typography>
                        )}
                        {errorMessage && (
                            <Typography color="error" variant="body2">
                                {errorMessage}
                            </Typography>
                        )}
                        <Box sx={{ flexGrow: 1 }} />
                        <Button onClick={handleClose} disabled={saving}>
                            Otkaži
                        </Button>
                        <Button variant="contained" onClick={handleSave} disabled={!canSave}>
                            {saving ? <CircularProgress size={15} color="inherit" /> : 'Sačuvaj sve'}
                        </Button>
                    </Grid2>
                </Grid2>
            </DialogContent>
        </ResponsiveDialog>
    );
}
